import clsx from 'clsx';
import Modal from '../Modal';

interface UrlInputConfig {
    prefix: string;
    suffix: string;
    placeholder: string;
}

interface IntegrationUrlModalProps {
    isOpen: boolean;
    onClose: () => void;
    toolName: string;
    urlConfig: UrlInputConfig;
    storeUrl: string;
    onStoreUrlChange: (value: string) => void;
    onConfirm: () => void;
    isLoading?: boolean;
}

export default function IntegrationUrlModal({
    isOpen,
    onClose,
    toolName,
    urlConfig,
    storeUrl,
    onStoreUrlChange,
    onConfirm,
    isLoading = false
}: IntegrationUrlModalProps) {
    // 如果未打开，不渲染任何内容
    if (!isOpen) return null;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && storeUrl.trim()) {
            onConfirm();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');

        // 根据配置的前缀和后缀截掉粘贴的文本
        let cleanedText = pastedText.trim();

        // 移除前缀
        if (urlConfig.prefix && cleanedText.startsWith(urlConfig.prefix)) {
            cleanedText = cleanedText.slice(urlConfig.prefix.length);
        }

        // 移除后缀
        if (urlConfig.suffix && cleanedText.endsWith(urlConfig.suffix)) {
            cleanedText = cleanedText.slice(0, -urlConfig.suffix.length);
        }

        // 移除可能的开头斜杠
        cleanedText = cleanedText.replace(/^\/+/, '').trim();

        onStoreUrlChange(cleanedText);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`Connect ${toolName}`}
            width="max-w-md"
        >
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-semibold text-text-main text-gray-900 mb-2">
                        {toolName}
                        {' '}
                        Store URL
                    </label>
                    <div className="flex items-center rounded-xl border border-gray-300 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-[#1A162F]/20 focus-within:border-[#1A162F] transition-all">
                        {urlConfig.prefix ? (
                            <span className="pl-3 pr-1 text-sm text-gray-500 whitespace-nowrap bg-gray-50/50 py-2.5 border-r border-gray-100 select-none">
                                {urlConfig.prefix}
                            </span>
                        ) : null}
                        <input
                            type="text"
                            value={storeUrl}
                            onChange={(e) => onStoreUrlChange(e.target.value)}
                            onPaste={handlePaste}
                            placeholder={urlConfig.placeholder}
                            className="flex-1 min-w-0 py-2.5 px-2 text-sm text-text-main text-gray-900 placeholder:text-gray-400 outline-none bg-transparent"
                            autoFocus
                            onKeyDown={handleKeyDown}
                        />
                        {urlConfig.suffix ? (
                            <span className="pr-3 pl-1 text-sm text-gray-500 whitespace-nowrap bg-gray-50/50 py-2.5 border-l border-gray-100 select-none">
                                {urlConfig.suffix}
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-text-sub text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={!storeUrl.trim() || isLoading}
                        className={clsx(
                            "px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2",
                            storeUrl.trim() && !isLoading
                                ? "bg-[#1A162F] text-white hover:bg-[#1A162F]/90"
                                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        )}
                    >
                        {isLoading ? (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : null}
                        Connect
                    </button>
                </div>
            </div>
        </Modal>
    );
}
